import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Flame,
  Car,
  TrendingUp,
  Star,
  ShieldCheck,
  ShieldAlert,
  Crown,
  ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  bronze:   { label: "Bronze · Active",        bg: "from-orange-700 to-orange-900",   pill: "bg-orange-600 text-white" },
  silver:   { label: "Silver · Reliable",      bg: "from-slate-500 to-slate-700",     pill: "bg-slate-500 text-white" },
  gold:     { label: "Gold · Professional",    bg: "from-amber-500 to-amber-700",     pill: "bg-amber-500 text-slate-900" },
  platinum: { label: "Platinum · Elite",       bg: "from-cyan-600 to-blue-800",       pill: "bg-cyan-500 text-slate-900" },
  black:    { label: "Black · Master Operator",bg: "from-zinc-800 to-black",          pill: "bg-zinc-900 text-amber-300 border border-amber-500/40" },
  none:     { label: "Building standards",     bg: "from-slate-700 to-slate-900",     pill: "bg-slate-700 text-slate-300" },
};

const ICON_MAP = {
  shield: Shield,
  flame: Flame,
  car: Car,
  trending: TrendingUp,
  star: Star,
  shieldcheck: ShieldCheck,
  crown: Crown,
};

/**
 * ProfessionalStandardsPanel — sober, premium card.
 *  - mode="public"  → only badges + tier pill (no raw %)
 *  - mode="private" → full breakdown + rank + open-dashboard CTA
 */
export default function ProfessionalStandardsPanel({
  instructorId,
  mode = "public",
  showLink = true,
}) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url =
          mode === "private"
            ? `${API}/api/standards/me`
            : `${API}/api/standards/for/${instructorId}`;
        const { data: d } = await axios.get(url, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch {
        /* silent — not critical */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instructorId, mode]);

  if (loading || !data) return null;

  const tier = data.stats?.tier || data.tier || "none";
  const meta = TIER_META[tier] || TIER_META.none;
  const badges = data.badges || [];

  // Public mode: only render if at least one badge, tier > none, OR compliant is true.
  if (
    mode === "public" &&
    tier === "none" &&
    badges.length === 0 &&
    !data.compliance?.is_compliant
  ) {
    return null;
  }

  return (
    <section
      data-testid="standards-panel"
      data-mode={mode}
      className={`rounded-2xl bg-gradient-to-br ${meta.bg} text-white p-5 shadow-xl`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-white/70">
            Operating Standards
          </p>
          <h3 data-testid="standards-tier" className="text-lg font-extrabold mt-0.5">
            {meta.label}
          </h3>
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          {mode === "private" && (
            <span
              data-testid="standards-score"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 text-white text-[11px] font-extrabold"
            >
              {data.stats?.score ?? 0} / 100
            </span>
          )}
          <CompliantPill compliance={data.compliance} mode={mode} />
        </div>
      </div>

      {mode === "private" && (
        <PrivateBreakdown stats={data.stats} ranking={data.ranking} />
      )}

      {badges.length > 0 && (
        <div data-testid="standards-badges" className="mt-4 flex flex-wrap gap-1.5">
          {badges.map((b) => (
            <BadgeChip key={b.key} badge={b} />
          ))}
        </div>
      )}

      {mode === "private" && showLink && (
        <button
          data-testid="standards-open-dashboard"
          onClick={() => nav("/instructor/standards")}
          className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/15 text-white text-[12px] font-extrabold hover:bg-white/25"
        >
          Open standards dashboard
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {mode === "private" && tier === "none" && (
        <p className="mt-3 text-[11px] text-white/70 leading-snug">
          Build a record of consistent daily and weekly checks for 14+ days to unlock your first tier.
        </p>
      )}

      {mode === "public" && (
        <p className="mt-3 text-[10px] text-white/60 leading-snug">
          Standards verified from real platform activity — no self-claims, no paid badges.
        </p>
      )}
    </section>
  );
}

function PrivateBreakdown({ stats, ranking }) {
  if (!stats) return null;
  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <Stat label="Daily completion" value={`${stats.daily_completion_pct}%`} sub={`${stats.daily_completed}/${stats.daily_window} days`} />
      <Stat label="Weekly completion" value={`${stats.weekly_completion_pct}%`} sub={`${stats.weekly_completed}/${stats.weekly_window} weeks`} />
      <Stat label="Skip rate" value={`${stats.skip_rate_pct}%`} sub="Last 90 days" />
      <Stat label="Daily streak" value={`${stats.current_daily_streak}`} sub="days running" />
      {ranking && ranking.national_top_pct !== undefined && (
        <>
          <Stat
            testid="standards-national-rank"
            label="National rank"
            value={`Top ${Math.max(1, ranking.national_top_pct)}%`}
            sub={`of ${ranking.national_total} active`}
          />
          {ranking.area_top_pct !== null && ranking.area_label && (
            <Stat
              testid="standards-area-rank"
              label={`In ${ranking.area_label}`}
              value={`Top ${Math.max(1, ranking.area_top_pct)}%`}
              sub="local instructors"
            />
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-white/10 p-2.5">
      <p className="text-[9px] font-extrabold uppercase tracking-wider text-white/60">{label}</p>
      <p className="text-base font-extrabold mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-white/60">{sub}</p>}
    </div>
  );
}

function BadgeChip({ badge }) {
  const Icon = ICON_MAP[badge.icon] || Star;
  return (
    <div
      data-testid={`standards-badge-${badge.key}`}
      title={badge.description}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-extrabold uppercase tracking-wider text-white"
    >
      <Icon className="w-3.5 h-3.5" /> {badge.label}
    </div>
  );
}

function CompliantPill({ compliance, mode }) {
  if (!compliance) return null;
  // Private mode: surface overdue/setup state too. Public: only show "Compliant" when truly so.
  if (compliance.is_compliant) {
    return (
      <span
        data-testid="standards-compliant-pill"
        title="MOT, insurance, vehicle tax and service all configured and up to date."
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 text-[10px] font-extrabold uppercase tracking-wider"
      >
        <ShieldCheck className="w-3.5 h-3.5" /> Compliant
      </span>
    );
  }
  if (mode === "private") {
    if (compliance.overdue_count > 0) {
      return (
        <span
          data-testid="standards-compliant-pill"
          data-state="overdue"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-100 text-[10px] font-extrabold uppercase tracking-wider"
        >
          <ShieldAlert className="w-3.5 h-3.5" /> {compliance.overdue_count} overdue
        </span>
      );
    }
    if (!compliance.configured) {
      return (
        <span
          data-testid="standards-compliant-pill"
          data-state="setup"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-[10px] font-extrabold uppercase tracking-wider"
        >
          <ShieldAlert className="w-3.5 h-3.5" /> Add renewals
        </span>
      );
    }
  }
  return null;
}
